import { lazy, Suspense } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'

import { CapabilityState } from '../components/CapabilityState'
import { Skeleton } from '../components/ui/primitives'
import { AgentConsoleLayout } from './AgentLayout'
import { Cab } from './pages/CabAndKb'

// Code splitting. The dashboard is the only route that draws charts, so Recharts
// is fetched on the dashboard rather than on first paint of any route; the grids
// pull in TanStack Table the same way, which is why the change list is lazy too
// even though it is small.
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Executive = lazy(() => import('./pages/Executive').then((m) => ({ default: m.Executive })))
const Reports = lazy(() => import('./pages/Reports').then((m) => ({ default: m.Reports })))
const AdminSubscriptions = lazy(() =>
  import('./pages/administration/Subscriptions').then((m) => ({ default: m.Subscriptions })),
)
const SystemHealth = lazy(() =>
  import('./pages/SystemHealth').then((m) => ({ default: m.SystemHealth })),
)

// Compliance Center — Platform Foundation phase 5. One control library, with
// SOC 2 / ISO 27001 / CMMI as views over it, so the three framework routes
// share a single component and cannot drift apart.
const ComplianceDashboard = lazy(() => import('./pages/compliance/Dashboard'))
const ComplianceFramework = lazy(() => import('./pages/compliance/Framework'))
const ComplianceControls = lazy(() => import('./pages/compliance/Controls'))
const ComplianceControlDetail = lazy(() =>
  import('./pages/compliance/Controls').then((m) => ({ default: m.ControlDetailPage })),
)
const ComplianceEvidence = lazy(() => import('./pages/compliance/Evidence'))
const ComplianceRisks = lazy(() => import('./pages/compliance/Risks'))
const ComplianceReadiness = lazy(() => import('./pages/compliance/Readiness'))
// Policies, Vendors, Continuity and Security Awareness were in the sidebar with
// no route behind them, so the only thing a reader could do with them was click
// and land nowhere. They are destinations now, and each says what it is waiting
// for — see docs/COMPLIANCE-BACKLOG.md.
const CompliancePlanned = lazy(() =>
  import('./pages/compliance/Planned').then((m) => ({ default: m.CompliancePlanned })),
)

// Email Center — Platform Foundation phase 6. The two queues are one component
// with different columns; five separate screens would have meant five places to
// fix the next time a delivery state was added.
const EmailDashboard = lazy(() => import('./pages/email/Dashboard'))
const EmailOutbound = lazy(() => import('./pages/email/Queues'))
const EmailInbound = lazy(() =>
  import('./pages/email/Queues').then((m) => ({ default: m.InboundQueuePage })),
)
const EmailRoutes = lazy(() => import('./pages/email/Rules'))
const EmailTemplates = lazy(() =>
  import('./pages/email/Rules').then((m) => ({ default: m.TemplatesPage })),
)
const CatalogAdmin = lazy(() =>
  import('./pages/CatalogAdmin').then((m) => ({ default: m.CatalogAdmin })),
)
const KnowledgeBase = lazy(() =>
  import('./pages/KnowledgeBase').then((m) => ({ default: m.KnowledgeBase })),
)
// The administration console is its own shell and its own tree; it shares only
// the design system with the desk, so it also gets its own chunk.
const AdminLayout = lazy(() =>
  import('./admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const AdministrationOverview = lazy(() =>
  import('./pages/administration/Overview').then((m) => ({ default: m.AdministrationOverview })),
)
const AdminPlaceholder = lazy(() =>
  import('./pages/administration/Placeholder').then((m) => ({ default: m.AdminPlaceholder })),
)
// System Health lives in the administration model but hangs off the desk's
// route tree, so it renders the same capability entry rather than a second copy
// of it that would drift.
const AdminOrganizations = lazy(() =>
  import('./pages/administration/Organizations').then((m) => ({ default: m.Organizations })),
)
// The settings API has served 56 per-organisation definitions since platform
// foundation 1 and nothing edited them; this destination pointed at the costing
// inventory instead. See pages/administration/SystemSettings.tsx.
const AdminSystemSettings = lazy(() =>
  import('./pages/administration/SystemSettings').then((m) => ({ default: m.SystemSettings })),
)
const AdminDeskSlas = lazy(() =>
  import('./pages/administration/DeskConfig').then((m) => ({ default: m.DeskSlas })),
)
const AdminDeskBusinessHours = lazy(() =>
  import('./pages/administration/DeskConfig').then((m) => ({ default: m.DeskBusinessHours })),
)
const AdminDeskPriorityRules = lazy(() =>
  import('./pages/administration/DeskConfig').then((m) => ({ default: m.DeskPriorityRules })),
)
const AdminUsers = lazy(() =>
  import('./pages/administration/Directory').then((m) => ({ default: m.AdminUsers })),
)
const AdminContacts = lazy(() =>
  import('./pages/administration/Directory').then((m) => ({ default: m.AdminContacts })),
)
const AdminCatalog = lazy(() =>
  import('./pages/administration/Catalog').then((m) => ({ default: m.AdminCatalog })),
)
const AdminRoles = lazy(() =>
  import('./pages/administration/Roles').then((m) => ({ default: m.AdminRoles })),
)
const AdminApiKeys = lazy(() =>
  import('./pages/administration/ApiKeys').then((m) => ({ default: m.ApiKeys })),
)
const AdminTeams = lazy(() =>
  import('./pages/administration/Directory').then((m) => ({ default: m.AdminTeams })),
)
const AdministrationCoverage = lazy(() =>
  import('./pages/Administration').then((m) => ({ default: m.Administration })),
)
// The audit console is heavy — a virtualizer, a diff viewer and four panels —
// and is opened by a minority of agents, so it keeps its own chunk.
const Audit = lazy(() => import('./pages/Audit').then((m) => ({ default: m.Audit })))

// The security console: the endpoint event log and the findings raised from it.
// Its own chunk, and a shared one — an operator who opens the log opens the
// findings beside it, and neither is on the path most agents take.
const SecurityEventLog = lazy(() =>
  import('./pages/security/EventLog').then((m) => ({ default: m.EventLog })),
)
const SecurityAlerts = lazy(() =>
  import('./pages/security/Alerts').then((m) => ({ default: m.SecurityAlerts })),
)

// Automation is three screens over one document model; they share a chunk
// because the designer, the templates and the overview all read the node
// catalog and nobody opens one without opening another.
const AutomationLayout = lazy(() =>
  import('./pages/automation/AutomationLayout').then((m) => ({ default: m.AutomationLayout })),
)
const AutomationHome = lazy(() =>
  import('./pages/automation/AutomationHome').then((m) => ({ default: m.AutomationHome })),
)
const WorkflowDesigner = lazy(() =>
  import('./pages/automation/WorkflowDesigner').then((m) => ({ default: m.WorkflowDesigner })),
)
const AutomationTemplates = lazy(() =>
  import('./pages/automation/AutomationTemplates').then((m) => ({ default: m.AutomationTemplates })),
)

// Assets is four screens over one CMDB; they share a chunk because they share
// the charts and the grid, and nobody opens one without opening another.
const AssetsLayout = lazy(() =>
  import('./pages/assets/AssetsLayout').then((m) => ({ default: m.AssetsLayout })),
)
const AssetDashboard = lazy(() =>
  import('./pages/assets/AssetDashboard').then((m) => ({ default: m.AssetDashboard })),
)
const AssetInventory = lazy(() =>
  import('./pages/assets/AssetInventory').then((m) => ({ default: m.AssetInventory })),
)
const AssetLifecycle = lazy(() =>
  import('./pages/assets/AssetLifecycle').then((m) => ({ default: m.AssetLifecycle })),
)
const AssetDetail = lazy(() =>
  import('./pages/assets/AssetDetail').then((m) => ({ default: m.AssetDetail })),
)
const AssetDiscovery = lazy(() =>
  import('./pages/assets/AssetDiscovery').then((m) => ({ default: m.AssetDiscovery })),
)
const Queue = lazy(() => import('./pages/Queue').then((m) => ({ default: m.Queue })))
const AgentTicketDetailPage = lazy(() =>
  import('./pages/TicketDetail').then((m) => ({ default: m.AgentTicketDetailPage })),
)
const ChangeList = lazy(() => import('./pages/Changes').then((m) => ({ default: m.ChangeList })))
const ChangeDetail = lazy(() =>
  import('./pages/Changes').then((m) => ({ default: m.ChangeDetail })),
)

function Loading() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

function split(element: React.ReactNode) {
  return <Suspense fallback={<Loading />}>{element}</Suspense>
}

// SPEC section 9. The gate guards the whole tree, so these need no guard.
const deskRoutes: RouteObject[] = [
  { index: true, element: split(<Dashboard />) },

  // Work
  { path: 'tickets', element: split(<Queue />) },
  { path: 'tickets/:ref', element: split(<AgentTicketDetailPage />) },
  { path: 'incidents', element: split(<Queue fixedType="incident" title="Incidents" />) },
  { path: 'requests', element: split(<Queue fixedType="request" title="Service requests" />) },
  { path: 'problems', element: split(<Queue fixedType="problem" title="Problems" />) },
  { path: 'changes', element: split(<ChangeList />) },
  { path: 'changes/:ref', element: split(<ChangeDetail />) },
  { path: 'cab', element: <Cab /> },
  { path: 'catalog', element: split(<CatalogAdmin />) },

  // Knowledge and assets
  { path: 'kb', element: split(<KnowledgeBase />) },
  {
    path: 'assets',
    element: split(<AssetsLayout />),
    children: [
      { index: true, element: split(<AssetDashboard />) },
      { path: 'inventory', element: split(<AssetInventory />) },
      { path: 'lifecycle', element: split(<AssetLifecycle />) },
      { path: 'discovery', element: split(<AssetDiscovery />) },
      { path: ':id', element: split(<AssetDetail />) },
    ],
  },

  // Executive
  { path: 'executive', element: split(<Executive />) },
  { path: 'reports', element: split(<Reports />) },
  { path: 'compliance', element: split(<ComplianceDashboard />) },
  // SOC 2, ISO 27001 and CMMI are the same component. A framework is a
  // projection over the shared control library, so three pages would be three
  // copies of one rendering that drift the first time somebody improves one.
  { path: 'compliance/soc2', element: split(<ComplianceFramework />) },
  { path: 'compliance/iso27001', element: split(<ComplianceFramework />) },
  { path: 'compliance/cmmi', element: split(<ComplianceFramework />) },
  { path: 'compliance/controls', element: split(<ComplianceControls />) },
  { path: 'compliance/controls/:id', element: split(<ComplianceControlDetail />) },
  { path: 'compliance/evidence', element: split(<ComplianceEvidence />) },
  { path: 'compliance/risks', element: split(<ComplianceRisks />) },
  { path: 'compliance/readiness', element: split(<ComplianceReadiness />) },
  { path: 'compliance/policies', element: split(<CompliancePlanned topic="policies" />) },
  { path: 'compliance/vendors', element: split(<CompliancePlanned topic="vendors" />) },
  { path: 'compliance/continuity', element: split(<CompliancePlanned topic="continuity" />) },
  { path: 'compliance/awareness', element: split(<CompliancePlanned topic="awareness" />) },

  // Email
  { path: 'email', element: split(<EmailDashboard />) },
  { path: 'email/outbound', element: split(<EmailOutbound />) },
  { path: 'email/inbound', element: split(<EmailInbound />) },
  { path: 'email/routes', element: split(<EmailRoutes />) },
  { path: 'email/templates', element: split(<EmailTemplates />) },
  // The compliance menu's audit entry predates the audit platform and was the
  // product's largest backend gap. Both halves exist now, so it points at the
  // console rather than at a description of what one would need.
  { path: 'compliance/audit', element: split(<Audit />) },

  // Security. Endpoint-generated events and the findings raised from them —
  // a different table, a different permission and a different audience from
  // Audit Logs below, which records what people did to this system.
  //
  // `security` alone redirects rather than 404s: the integration card, the
  // sidebar and anything anybody has bookmarked all point at a child, and a
  // bare parent that answers nothing is the kind of dead end nobody notices
  // until an operator hits it.
  { path: 'security', element: <Navigate to="/agent/security/events" replace /> },
  { path: 'security/events', element: split(<SecurityEventLog />) },
  { path: 'security/alerts', element: split(<SecurityAlerts />) },
  {
    path: 'bi',
    element: (
      <CapabilityState
        title="Business Intelligence"
        description="Self-service exploration and external BI tooling."
        summary="The exploration surface is designed; nothing in the API can serve a dataset large enough for it."
        available={[
          'Reports and the executive overview derive every figure from the queue feed, and refuse any window the sample cannot cover',
          'CSV export of the ticket grid, for a one-off pull into a spreadsheet',
        ]}
        missing={[
          'A paginated, uncapped read feed — every figure in Reports is bounded by the queue API’s 200-row slice',
          'Token or service-principal auth, so Power BI and Fabric can read without a browser session',
          'A saved-query model, if exploration is to be shareable',
        ]}
        api={[
          'GET /api/agent/analytics/tickets/?cursor= (uncapped, cursor-paginated)',
          'POST /api/agent/service-principals/ (token issue and revoke)',
        ]}
        unlocks={[
          'Point Power BI or Fabric at the desk without a nightly database dump',
          'Build and share a query without a release',
        ]}
      />
    ),
  },
  { path: 'system-health', element: split(<SystemHealth />) },
  {
    path: 'automation',
    element: split(<AutomationLayout />),
    children: [
      { index: true, element: split(<AutomationHome />) },
      { path: 'designer', element: split(<WorkflowDesigner />) },
      { path: 'templates', element: split(<AutomationTemplates />) },
    ],
  },

  // System
  { path: 'audit', element: split(<Audit />) },
  {
    path: 'settings',
    element: (
      <CapabilityState
        title="Settings"
        description="Your preferences and notification choices."
        summary="Personal preferences have no store. Organisation configuration is a different screen and does exist — Administration → System Settings."
        available={[
          'Saved queue filters persist per agent at GET/POST /api/agent/filters/',
          'Theme and sidebar state are kept in the browser, not on the server',
        ]}
        missing={[
          'A per-agent preferences endpoint; only saved queue filters are persisted today',
          'Notification channel preferences — outbound mail has no per-user opt-in model',
        ]}
        api={['GET/PATCH /api/agent/me/preferences/', 'GET/PATCH /api/agent/me/notifications/']}
        unlocks={[
          'Choose a default queue, density and landing screen that follow you between browsers',
          'Mute a notification you do not need without leaving the desk',
        ]}
      />
    ),
  },
]

/** Two shells under one gate.
 *
 * Administration is not a screen inside the service desk — it has its own top
 * bar, its own rail and its own information architecture — so it hangs beside
 * the desk's shell rather than inside it. A pathless layout route wraps the
 * desk; `administration` wraps itself.
 */
export const agentRoutes: RouteObject[] = [
  {
    path: 'administration',
    element: split(<AdminLayout />),
    children: [
      { index: true, element: split(<AdministrationOverview />) },
      // The settings-coverage inventory: which of the 80-odd settings are
      // reachable, and what each of the rest would take.
      { path: 'coverage', element: split(<AdministrationCoverage />) },
      // Organizations is the one pending screen with a real read behind it, so
      // it gets a component rather than the generic capability state.
      { path: 'organizations', element: split(<AdminOrganizations />) },
      { path: 'system-settings', element: split(<AdminSystemSettings />) },
      // Notification audiences are edited on the settings screen. The rail
      // keeps a Notifications row because that is what administrators look
      // for, and a row's destination has to resolve back to its own slug.
      { path: 'notifications', element: split(<AdminSystemSettings />) },
      { path: 'slas', element: split(<AdminDeskSlas />) },
      { path: 'business-hours', element: split(<AdminDeskBusinessHours />) },
      { path: 'priority-rules', element: split(<AdminDeskPriorityRules />) },
      { path: 'users', element: split(<AdminUsers />) },
      { path: 'contacts', element: split(<AdminContacts />) },
      { path: 'roles', element: split(<AdminRoles />) },
      { path: 'services', element: split(<AdminCatalog />) },
      { path: 'service-categories', element: split(<AdminCatalog />) },
      // Ticket Forms is the same screen: a request form belongs to the
      // catalog item it is for, so authoring one is editing that item.
      // Its own path, because the rail's contract is that a row's
      // destination resolves back to that row's slug.
      { path: 'ticket-forms', element: split(<AdminCatalog />) },
      { path: 'teams', element: split(<AdminTeams />) },
      { path: 'integrations', element: split(<AdminApiKeys />) },
      { path: 'subscriptions', element: split(<AdminSubscriptions />) },
      // Everything else in the rail. Static paths above rank ahead of this.
      { path: ':slug', element: split(<AdminPlaceholder />) },
    ],
  },
  { element: <AgentConsoleLayout />, children: deskRoutes },
]
